import {
  AlertTriangle,
  Server,
  Code,
  Package,
  Palette,
} from 'lucide-react';
import type { HealthCheck } from '../types';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface HealthCardProps {
  healthCheck: HealthCheck;
}

export default function HealthCard({ healthCheck }: HealthCardProps) {
  const items = [
    {
      icon: Server,
      label: 'WordPress',
      value: healthCheck.wpVersion || '-',
      warning: healthCheck.isWpOutdated,
      warningText: healthCheck.wpLatestVersion
        ? `Update auf ${healthCheck.wpLatestVersion} verfügbar`
        : undefined,
    },
    {
      icon: Code,
      label: 'PHP',
      value: healthCheck.phpVersion || '-',
      warning: healthCheck.isPhpOutdated,
      warningText: 'PHP Version veraltet (min. 8.0)',
    },
    {
      icon: Package,
      label: 'Plugin Updates',
      value: healthCheck.pluginUpdates.toString(),
      warning: healthCheck.pluginUpdates > 5,
      warningText: 'Mehr als 5 Plugin-Updates ausstehend',
    },
    {
      icon: Palette,
      label: 'Theme Updates',
      value: healthCheck.themeUpdates.toString(),
      warning: healthCheck.themeUpdates > 2,
      warningText: 'Theme-Updates ausstehend',
    },
  ];

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Health Status</h3>
        <span className="text-sm text-gray-500">
          Letzte Prüfung:{' '}
          {formatDistanceToNow(new Date(healthCheck.createdAt), {
            addSuffix: true,
            locale: de,
          })}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((item) => (
          <div
            key={item.label}
            className={clsx(
              'p-4 rounded-lg',
              item.warning ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <item.icon
                className={clsx(
                  'w-5 h-5',
                  item.warning ? 'text-yellow-500' : 'text-gray-400'
                )}
              />
              <span className="text-sm text-gray-600">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{item.value}</span>
              {item.warning && (
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
              )}
            </div>
            {item.warning && item.warningText && (
              <p className="text-xs text-yellow-600 mt-1">{item.warningText}</p>
            )}
          </div>
        ))}
      </div>

      {healthCheck.lastLoginAt && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-600">
            Letzter Admin-Login:{' '}
            {formatDistanceToNow(new Date(healthCheck.lastLoginAt), {
              addSuffix: true,
              locale: de,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
